/**
 * File system implementations for reading splat data from various sources.
 */

import {
    BufferedReadStream,
    ReadFileSystem,
    ReadSource,
    ReadStream,
    ProgressCallback
} from '@playcanvas/splat-transform';

// Read blob in 4MB chunks to balance async overhead vs memory usage
const BLOB_CHUNK_SIZE = 4 * 1024 * 1024;

/**
 * Every splat byte read bypasses the HTTP cache.
 *
 * splat-transform's UrlReadFileSystem probes range support with a `Range: bytes=0-0` GET that it
 * aborts as soon as headers arrive. Chromium has already begun a sparse cache entry for that URL;
 * the abort dooms it, and every later ranged read of the same URL fails with
 * net::ERR_CACHE_OPERATION_NOT_SUPPORTED. Splat URLs are signed and cache-busted per load, so the
 * HTTP cache never had anything to offer them.
 */
const NO_STORE: RequestInit = { cache: 'no-store' };

/**
 * ReadStream implementation for reading from Blob/File.
 */
class BlobReadStream extends ReadStream {
    private blob: Blob;
    private offset: number;
    private end: number;

    constructor(blob: Blob, start: number, end: number) {
        super(end - start);
        this.blob = blob;
        this.offset = start;
        this.end = end;
    }

    async pull(target: Uint8Array): Promise<number> {
        const remaining = this.end - this.offset;
        if (remaining <= 0) {
            return 0;
        }

        const bytesToRead = Math.min(target.length, remaining);
        const slice = this.blob.slice(this.offset, this.offset + bytesToRead);
        const arrayBuffer = await slice.arrayBuffer();
        target.set(new Uint8Array(arrayBuffer));
        this.offset += bytesToRead;
        this.bytesRead += bytesToRead;
        return bytesToRead;
    }
}

/**
 * ReadSource implementation for Blob/File.
 */
class BlobReadSource implements ReadSource {
    readonly size: number;
    readonly seekable: boolean = true;

    private blob: Blob;
    private closed: boolean = false;

    constructor(blob: Blob) {
        this.blob = blob;
        this.size = blob.size;
    }

    read(start: number = 0, end: number = this.size): ReadStream {
        if (this.closed) {
            throw new Error('Source has been closed');
        }

        const clampedStart = Math.max(0, Math.min(start, this.size));
        const clampedEnd = Math.max(clampedStart, Math.min(end, this.size));

        // Wrap with BufferedReadStream to reduce async overhead from blob reads
        const raw = new BlobReadStream(this.blob, clampedStart, clampedEnd);
        return new BufferedReadStream(raw, BLOB_CHUNK_SIZE);
    }

    close(): void {
        this.closed = true;
    }
}

/**
 * ReadFileSystem for reading from browser File/Blob objects.
 * Used for drag & drop and file picker scenarios.
 */
class BlobReadFileSystem implements ReadFileSystem {
    private files: Map<string, Blob> = new Map();

    /**
     * Add a file to the file system.
     */
    set(name: string, blob: Blob): void {
        this.files.set(name.toLowerCase(), blob);
    }

    /**
     * Get a file by name.
     */
    get(name: string): Blob | undefined {
        return this.files.get(name.toLowerCase());
    }

    createSource(filename: string, progress?: ProgressCallback): Promise<ReadSource> {
        const blob = this.files.get(filename.toLowerCase());
        if (!blob) {
            return Promise.reject(new Error(`File not found: ${filename}`));
        }
        if (progress) {
            progress(blob.size, blob.size);
        }
        return Promise.resolve(new BlobReadSource(blob));
    }
}

/** Streams one fetch response, reporting progress against the whole file rather than the range. */
class NoStoreUrlReadStream extends ReadStream {
    private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    private leftover: Uint8Array | null = null;
    private leftoverOffset = 0;
    private opening: Promise<void> | null = null;
    private closed = false;

    constructor(
        private readonly url: string,
        private readonly range: string | null,
        expectedSize: number | undefined,
        private readonly onBytes?: (delta: number) => void
    ) {
        super(expectedSize);
    }

    private open(): Promise<void> {
        if (!this.opening) {
            this.opening = (async () => {
                const init: RequestInit = this.range
                    ? { ...NO_STORE, headers: { Range: this.range } }
                    : { ...NO_STORE };
                const response = await fetch(this.url, init);
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
                }
                this.reader = response.body?.getReader() ?? null;
            })();
        }
        return this.opening;
    }

    private take(target: Uint8Array, offset: number, chunk: Uint8Array, chunkOffset: number): number {
        const toCopy = Math.min(chunk.length - chunkOffset, target.length - offset);
        target.set(chunk.subarray(chunkOffset, chunkOffset + toCopy), offset);
        this.bytesRead += toCopy;
        this.onBytes?.(toCopy);
        return toCopy;
    }

    async pull(target: Uint8Array): Promise<number> {
        if (this.closed) return 0;
        await this.open();
        if (this.closed || !this.reader) return 0;

        let offset = 0;

        if (this.leftover) {
            const copied = this.take(target, offset, this.leftover, this.leftoverOffset);
            offset += copied;
            this.leftoverOffset += copied;
            if (this.leftoverOffset >= this.leftover.length) {
                this.leftover = null;
                this.leftoverOffset = 0;
            }
            if (offset >= target.length) return offset;
        }

        while (offset < target.length) {
            const { done, value } = await this.reader.read();
            if (done || !value) break;
            const copied = this.take(target, offset, value, 0);
            offset += copied;
            if (copied < value.length) {
                this.leftover = value;
                this.leftoverOffset = copied;
                break;
            }
        }

        return offset;
    }

    close(): void {
        this.closed = true;
        this.reader?.cancel().catch(() => { /* stream already torn down */ });
        this.reader = null;
        this.leftover = null;
    }
}

/** Seekable URL source issuing `no-store` ranged GETs. */
class NoStoreUrlReadSource implements ReadSource {
    readonly seekable = true;

    private closed = false;
    private loaded = 0;

    constructor(
        readonly size: number | undefined,
        private readonly url: string,
        private readonly progress?: ProgressCallback
    ) {}

    read(start?: number, end?: number): ReadStream {
        if (this.closed) {
            throw new Error('Source has been closed');
        }

        const ranged = start !== undefined || end !== undefined;
        const rangeStart = start ?? 0;
        const rangeEnd = end ?? this.size;
        const expectedSize = rangeEnd === undefined ? undefined : rangeEnd - rangeStart;
        // HTTP byte ranges are inclusive; an open-ended range omits the final offset.
        const range = ranged ? `bytes=${rangeStart}-${end !== undefined ? end - 1 : ''}` : null;

        const onBytes = this.progress
            ? (delta: number) => {
                this.loaded += delta;
                this.progress?.(Math.min(this.loaded, this.size ?? this.loaded), this.size);
            }
            : undefined;

        return new NoStoreUrlReadStream(this.url, range, expectedSize, onBytes);
    }

    close(): void {
        this.closed = true;
    }
}

/**
 * Drop-in replacement for splat-transform's UrlReadFileSystem that never touches the HTTP cache.
 * The range probe consumes its one-byte body instead of aborting, so no sparse cache entry is
 * left behind for subsequent reads to trip over.
 */
class NoStoreUrlReadFileSystem implements ReadFileSystem {
    constructor(private readonly baseUrl: string = '') {}

    private static async probeRangeSupport(url: string): Promise<{ supported: boolean; size?: number }> {
        try {
            const response = await fetch(url, { ...NO_STORE, headers: { Range: 'bytes=0-0' } });
            await response.arrayBuffer();
            if (response.status !== 206) {
                return { supported: false };
            }
            const match = response.headers.get('Content-Range')?.match(/\/(\d+)$/);
            return { supported: true, size: match ? parseInt(match[1], 10) : undefined };
        } catch {
            return { supported: false };
        }
    }

    async createSource(filename: string, progress?: ProgressCallback): Promise<ReadSource> {
        const url = this.baseUrl ? new URL(filename, this.baseUrl).href : filename;

        const { supported, size: probedSize } = await NoStoreUrlReadFileSystem.probeRangeSupport(url);
        if (!supported) {
            return await this.createMemorySource(url, progress);
        }

        let size = probedSize;
        if (size === undefined) {
            const head = await fetch(url, { ...NO_STORE, method: 'HEAD' });
            const contentLength = head.ok ? head.headers.get('Content-Length') : null;
            size = contentLength ? parseInt(contentLength, 10) : undefined;
        }

        progress?.(0, size);
        return new NoStoreUrlReadSource(size, url, progress);
    }

    /** Whole-file download for servers that ignore Range. */
    private async createMemorySource(url: string, progress?: ProgressCallback): Promise<ReadSource> {
        const response = await fetch(url, NO_STORE);
        if (!response.ok) {
            throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
        }

        const contentLength = response.headers.get('Content-Length');
        const expectedSize = contentLength ? parseInt(contentLength, 10) : undefined;
        progress?.(0, expectedSize);

        if (!response.body || !progress) {
            const buffer = await response.arrayBuffer();
            progress?.(buffer.byteLength, buffer.byteLength);
            return new BlobReadSource(new Blob([buffer]));
        }

        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let loaded = 0;
        for (;;) {
            const { done, value } = await reader.read();
            if (done || !value) break;
            chunks.push(value);
            loaded += value.length;
            progress(loaded, expectedSize);
        }

        return new BlobReadSource(new Blob(chunks as BlobPart[]));
    }
}

/**
 * ReadFileSystem that combines URL-based loading with local file storage.
 * Used for multi-file formats (SOG, LCC) where some files may be local
 * and others may need to be fetched from URLs.
 */
class MappedReadFileSystem implements ReadFileSystem {
    private blobFs: BlobReadFileSystem;
    private urlFs: NoStoreUrlReadFileSystem;

    constructor(baseUrl?: string) {
        this.blobFs = new BlobReadFileSystem();
        this.urlFs = new NoStoreUrlReadFileSystem(baseUrl);
    }

    /**
     * Add a local file.
     */
    addFile(name: string, blob: Blob): void {
        this.blobFs.set(name, blob);
    }

    async createSource(filename: string, progress?: ProgressCallback): Promise<ReadSource> {
        // First check if we have a local blob
        const localBlob = this.blobFs.get(filename);
        if (localBlob) {
            if (progress) {
                progress(localBlob.size, localBlob.size);
            }
            return new BlobReadSource(localBlob);
        }

        // Fall back to URL loading
        return await this.urlFs.createSource(filename, progress);
    }
}

/** Firebase Storage download-token bundle reads (private streamed LOD chunks). */
class TokenizedStorageReadFileSystem implements ReadFileSystem {
    private bucket: string;
    private prefix: string;
    private token: string;
    private pathPrefix: string;
    private urlFs = new NoStoreUrlReadFileSystem();

    constructor(bundleBase: { bucket: string; prefix: string; token: string; pathPrefix?: string }) {
        this.bucket = bundleBase.bucket;
        this.prefix = bundleBase.prefix.replace(/\/+$/, '');
        this.token = bundleBase.token;
        this.pathPrefix = typeof bundleBase.pathPrefix === 'string' ? bundleBase.pathPrefix : '';
    }

    private urlFor(filename: string): string {
        const normalized = filename.replace(/^\/+/, '');
        const isBundleIndex = normalized.toLowerCase().endsWith('lod-meta.json');
        const rel = isBundleIndex ? normalized : `${this.pathPrefix}${normalized}`.replace(/\/+/g, '/');
        const path = `${this.prefix}/${rel}`.replace(/\/+/g, '/');
        const encoded = encodeURIComponent(path);
        return `https://firebasestorage.googleapis.com/v0/b/${this.bucket}/o/${encoded}?alt=media&token=${encodeURIComponent(this.token)}`;
    }

    createSource(filename: string, progress?: ProgressCallback): Promise<ReadSource> {
        return this.urlFs.createSource(this.urlFor(filename), progress);
    }
}

export {
    BlobReadSource,
    MappedReadFileSystem,
    NoStoreUrlReadFileSystem,
    TokenizedStorageReadFileSystem
};
